import React, { useCallback, useEffect, useRef, useState } from "react";
import { useBeforeUnload } from "react-router-dom";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Tab,
  Tabs,
  Typography,
  Box,
} from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import { useAirframeTemplateService } from "../api/airframeTemplateService";
import { useSnackbar } from "../contexts/snackbar-context";
import { formatFileSize } from "../services/fileValidationService";
import {
  AssetTemplateFileType,
  BulkTemplateUploadProcessResult,
  BulkTemplateUploadRowResult,
  BulkTemplateUploadValidationResult,
  FolderProvisioningStatus,
} from "../types/airframeTemplate";

const templateFileTypes: Array<{
  value: AssetTemplateFileType;
  label: string;
}> = [
  { value: "aircraft", label: "Aircraft" },
  { value: "engine", label: "Engine" },
  { value: "apu", label: "APU" },
  { value: "landing gear", label: "Landing Gear" },
  { value: "landing gear sub-assembly", label: "LandingGear Sub-Assembly" },
];

const excelMimeType =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const reviewPageSizeOptions = [10, 25, 50] as const;

const FOLDER_PROCESSING_KEY = "pontis_bulk_folder_processing";
const FOLDER_PROCESSING_TOTAL_KEY = "pontis_bulk_folder_processing_total";
const FOLDER_PROCESSING_BASELINE_KEY = "pontis_bulk_folder_processing_baseline";
const POLL_INTERVAL_MS = 10_000;

const parseStoredNumber = (value: string | null): number => {
  if (!value) {
    return 0;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const parseStoredStatus = (value: string | null): FolderProvisioningStatus | null => {
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value) as FolderProvisioningStatus;

    if (
      typeof parsed.pendingCount === "number" &&
      typeof parsed.completedCount === "number" &&
      typeof parsed.failedCount === "number" &&
      typeof parsed.totalCount === "number"
    ) {
      return parsed;
    }
  } catch {
    return null;
  }

  return null;
};

const toUploadScopedFolderStatus = (
  current: FolderProvisioningStatus,
  baseline: FolderProvisioningStatus,
  expectedTotal: number
): FolderProvisioningStatus => {
  const completedDelta = Math.max((current.completedCount ?? 0) - (baseline.completedCount ?? 0), 0);
  const failedDelta = Math.max((current.failedCount ?? 0) - (baseline.failedCount ?? 0), 0);
  const pendingDelta = Math.max((current.pendingCount ?? 0) - (baseline.pendingCount ?? 0), 0);

  const completedFromPending = Math.max(expectedTotal - pendingDelta - failedDelta, 0);
  const completed = Math.max(completedDelta, completedFromPending);

  const cappedCompleted = Math.min(completed, expectedTotal);
  const cappedFailed = Math.min(failedDelta, Math.max(expectedTotal - cappedCompleted, 0));
  const pendingCount = Math.max(expectedTotal - cappedCompleted - cappedFailed, 0);

  return {
    pendingCount,
    completedCount: cappedCompleted,
    failedCount: cappedFailed,
    totalCount: expectedTotal,
  };
};

const getSelectedFileTypeLabel = (fileType: AssetTemplateFileType) =>
  templateFileTypes.find((item) => item.value === fileType)?.label || "Asset";

const getColumnValue = (
  row: BulkTemplateUploadRowResult,
  key: string
): string => row.columns.find((column) => column.key === key)?.value || "";

const getRowTitle = (row: BulkTemplateUploadRowResult) => {
  const serialNumber = getColumnValue(row, "serialNumber");

  if (serialNumber) {
    return serialNumber;
  }

  const manufacturer = getColumnValue(row, "manufacturer");
  const model = getColumnValue(row, "model");
  return [manufacturer, model].filter(Boolean).join(" ") || "Record details";
};

const BulkTemplateUploadPage: React.FC = () => {
  const isPollingRef = useRef(false);
  const suppressNextPopStateRef = useRef(false);
  const { showSuccess, showError } = useSnackbar();
  const {
    downloadAssetTemplate,
    validateBulkTemplateUpload,
    processBulkTemplateUpload,
    getFolderProvisioningStatus,
  } = useAirframeTemplateService();
  const [downloading, setDownloading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [uploadedWorkbook, setUploadedWorkbook] = useState<File | null>(null);
  const [selectedFileType, setSelectedFileType] =
    useState<AssetTemplateFileType | null>(null);
  const [reviewTab, setReviewTab] = useState(0);
  const [reviewPage, setReviewPage] = useState(1);
  const [reviewPageSize, setReviewPageSize] = useState<number>(10);
  const [validationResult, setValidationResult] =
    useState<BulkTemplateUploadValidationResult | null>(null);
  const [processResult, setProcessResult] =
    useState<BulkTemplateUploadProcessResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // ── Folder provisioning tracking ──
  const [folderStatus, setFolderStatus] = useState<FolderProvisioningStatus | null>(null);
  const [folderExpectedTotal, setFolderExpectedTotal] = useState<number>(() =>
    parseStoredNumber(localStorage.getItem(FOLDER_PROCESSING_TOTAL_KEY))
  );
  const [folderBaselineStatus, setFolderBaselineStatus] = useState<FolderProvisioningStatus | null>(() =>
    parseStoredStatus(localStorage.getItem(FOLDER_PROCESSING_BASELINE_KEY))
  );
  const [folderProcessingActive, setFolderProcessingActive] = useState(
    () => localStorage.getItem(FOLDER_PROCESSING_KEY) === "true"
  );
  const navigationGuardActive = processing || folderProcessingActive;

  useEffect(() => {
    if (!navigationGuardActive) {
      return;
    }

    const confirmMessage =
      "Folder provisioning is still running in the background. You can navigate away and return later. Do you want to leave this page?";

    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }

      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) {
        return;
      }

      if (anchor.target && anchor.target !== "_self") {
        return;
      }

      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#")) {
        return;
      }

      let destination: URL;
      try {
        destination = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }

      if (destination.origin !== window.location.origin) {
        return;
      }

      const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const nextPath = `${destination.pathname}${destination.search}${destination.hash}`;

      if (currentPath === nextPath) {
        return;
      }

      const shouldLeave = window.confirm(confirmMessage);
      if (!shouldLeave) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    const shouldAllowPathChange = (nextUrl?: string | URL | null): boolean => {
      if (!nextUrl) {
        return true;
      }

      let destination: URL;
      try {
        destination = new URL(nextUrl.toString(), window.location.href);
      } catch {
        return true;
      }

      if (destination.origin !== window.location.origin) {
        return true;
      }

      const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const nextPath = `${destination.pathname}${destination.search}${destination.hash}`;

      if (currentPath === nextPath) {
        return true;
      }

      return window.confirm(confirmMessage);
    };

    const handlePopState = () => {
      if (suppressNextPopStateRef.current) {
        suppressNextPopStateRef.current = false;
        return;
      }

      const shouldLeave = window.confirm(confirmMessage);
      if (!shouldLeave) {
        suppressNextPopStateRef.current = true;
        window.history.go(1);
      }
    };

    const historyApi = window.history as History & {
      pushState: History["pushState"];
      replaceState: History["replaceState"];
    };
    const originalPushState = historyApi.pushState.bind(historyApi);
    const originalReplaceState = historyApi.replaceState.bind(historyApi);

    historyApi.pushState = ((
      data: unknown,
      unused: string,
      url?: string | URL | null
    ) => {
      if (!shouldAllowPathChange(url)) {
        return;
      }

      originalPushState(data, unused, url);
    }) as History["pushState"];

    historyApi.replaceState = ((
      data: unknown,
      unused: string,
      url?: string | URL | null
    ) => {
      if (!shouldAllowPathChange(url)) {
        return;
      }

      originalReplaceState(data, unused, url);
    }) as History["replaceState"];

    document.addEventListener("click", handleDocumentClick, true);
    window.addEventListener("popstate", handlePopState);
    return () => {
      document.removeEventListener("click", handleDocumentClick, true);
      window.removeEventListener("popstate", handlePopState);
      historyApi.pushState = originalPushState;
      historyApi.replaceState = originalReplaceState;
    };
  }, [navigationGuardActive]);

  useBeforeUnload(
    useCallback(
      (event: BeforeUnloadEvent) => {
        if (!navigationGuardActive) {
          return;
        }

        event.preventDefault();
        event.returnValue = "";
      },
      [navigationGuardActive]
    )
  );

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const expectedTotalRef = useRef<number>(folderExpectedTotal);
  const baselineStatusRef = useRef<FolderProvisioningStatus | null>(folderBaselineStatus);

  useEffect(() => {
    expectedTotalRef.current = folderExpectedTotal;
  }, [folderExpectedTotal]);

  useEffect(() => {
    baselineStatusRef.current = folderBaselineStatus;
  }, [folderBaselineStatus]);

  const clearFolderProcessingState = useCallback(() => {
    localStorage.removeItem(FOLDER_PROCESSING_KEY);
    localStorage.removeItem(FOLDER_PROCESSING_TOTAL_KEY);
    localStorage.removeItem(FOLDER_PROCESSING_BASELINE_KEY);
    setFolderExpectedTotal(0);
    setFolderBaselineStatus(null);
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const resetFolderProcessingTracking = useCallback(() => {
    stopPolling();
    setFolderProcessingActive(false);
    setFolderStatus(null);
    clearFolderProcessingState();
  }, [clearFolderProcessingState, stopPolling]);

const pollFolderStatus = useCallback(async (): Promise<boolean> => {
  if (isPollingRef.current) return false;

  isPollingRef.current = true;

  try {
    const status = await getFolderProvisioningStatus();
    setFolderStatus(status);

    const expectedTotal = expectedTotalRef.current;
    const baselineStatus = baselineStatusRef.current;

    if (expectedTotal > 0 && baselineStatus) {
      const scopedStatus = toUploadScopedFolderStatus(status, baselineStatus, expectedTotal);

      if (scopedStatus.pendingCount === 0) {
        stopPolling();
        setFolderProcessingActive(false);
        clearFolderProcessingState();
        return true;
      }

      return false;
    }

    if (status.pendingCount === 0) {
      stopPolling();
      setFolderProcessingActive(false);
      clearFolderProcessingState();
      return true;
    }
    return false;
  } catch {
    // ignore
    return false;
  } finally {
    isPollingRef.current = false; 
  }
}, [clearFolderProcessingState, getFolderProvisioningStatus, stopPolling]);

  const startPolling = useCallback(async (
    expectedTotal: number,
    baselineOverride?: FolderProvisioningStatus | null
  ) => {
    if (expectedTotal <= 0) {
      return;
    }

    stopPolling();
    setFolderProcessingActive(true);
    localStorage.setItem(FOLDER_PROCESSING_KEY, "true");
    localStorage.setItem(FOLDER_PROCESSING_TOTAL_KEY, String(expectedTotal));
    setFolderExpectedTotal(expectedTotal);

    let baselineStatus = baselineOverride;
    if (!baselineStatus) {
      try {
        baselineStatus = await getFolderProvisioningStatus();
      } catch {
        // Keep processing state active even if baseline snapshot fails.
        baselineStatus = {
          pendingCount: 0,
          completedCount: 0,
          failedCount: 0,
          totalCount: 0,
        };
      }
    }

    setFolderBaselineStatus(baselineStatus);
    localStorage.setItem(FOLDER_PROCESSING_BASELINE_KEY, JSON.stringify(baselineStatus));

    // Trigger first poll asynchronously so processing state can render first.
    setTimeout(() => {
      void pollFolderStatus();
    }, 0);

    pollRef.current = setInterval(() => {
      void pollFolderStatus();
    }, POLL_INTERVAL_MS);
  }, [getFolderProvisioningStatus, pollFolderStatus, stopPolling]);

  // resume polling if localStorage says we were processing
useEffect(() => {
  if (
    localStorage.getItem(FOLDER_PROCESSING_KEY) === "true" &&
    !pollRef.current 
  ) {
    const storedTotal = parseStoredNumber(localStorage.getItem(FOLDER_PROCESSING_TOTAL_KEY));

    if (storedTotal <= 0) {
      setFolderProcessingActive(false);
      clearFolderProcessingState();
      return () => stopPolling();
    }

    const storedBaseline = parseStoredStatus(localStorage.getItem(FOLDER_PROCESSING_BASELINE_KEY));

    setFolderProcessingActive(true);
    setReviewTab(2);
    setFolderExpectedTotal(storedTotal);
    if (storedBaseline) {
      setFolderBaselineStatus(storedBaseline);
    }
    void pollFolderStatus();

    pollRef.current = setInterval(() => {
      void pollFolderStatus();
    }, POLL_INTERVAL_MS);
  }

  return () => stopPolling();
}, [clearFolderProcessingState, pollFolderStatus, stopPolling]);

  useEffect(() => {
    if (folderProcessingActive) {
      setReviewTab((current) => (current === 2 ? current : 2));
      return;
    }

    setReviewTab((current) => {
      if (current !== 2) {
        return current;
      }

      return (validationResult?.failedCount ?? 0) > 0 ? 1 : 0;
    });
  }, [folderProcessingActive, validationResult]);

  const scopedFolderStatus =
    folderStatus && folderBaselineStatus && folderExpectedTotal > 0
      ? toUploadScopedFolderStatus(folderStatus, folderBaselineStatus, folderExpectedTotal)
      : folderStatus;

  const activeRows = reviewTab === 0
    ? (validationResult?.rows || []).filter((row) => row.status === "Passed")
    : reviewTab === 1
      ? (validationResult?.rows || []).filter((row) => row.status === "Failed")
      : [];
  const reviewPageCount = Math.max(1, Math.ceil(activeRows.length / reviewPageSize));
  const pagedRows = activeRows.slice(
    (reviewPage - 1) * reviewPageSize,
    reviewPage * reviewPageSize
  );

  useEffect(() => {
    setReviewPage(1);
  }, [reviewTab, reviewPageSize, validationResult]);

  useEffect(() => {
    if (reviewPage <= reviewPageCount) {
      return;
    }

    setReviewPage(reviewPageCount);
  }, [reviewPage, reviewPageCount]);

  const handleValidateWorkbook = async (file: File) => {
    if (!selectedFileType) {
      showError("Please select a file type first.");
      return;
    }

    setValidating(true);

    try {
      const result = await validateBulkTemplateUpload(selectedFileType, file);
      setValidationResult(result);
      setProcessResult(null);
      setReviewTab(result.failedCount > 0 ? 1 : 0);
      setReviewPage(1);
      showSuccess(
        `Validation completed: ${result.passedCount} passed, ${result.failedCount} failed.`
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to validate workbook.";
      showError(message);
    } finally {
      setValidating(false);
    }
  };

  const handleWorkbookSelected = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0] || null;

    if (!file) {
      return;
    }

    const isExcelFile =
      file.name.toLowerCase().endsWith(".xlsx") || file.type === excelMimeType;

    if (!isExcelFile) {
      setUploadedWorkbook(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      showError("Only .xlsx files are allowed.");
      return;
    }

    setUploadedWorkbook(file);
    resetFolderProcessingTracking();
    setValidationResult(null);
    setProcessResult(null);
    showSuccess("Workbook selected successfully.");
    await handleValidateWorkbook(file);
  };

  const handleRemoveWorkbook = () => {
    setUploadedWorkbook(null);

    resetFolderProcessingTracking();

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    setValidationResult(null);
    setProcessResult(null);
  };

  const handleDownloadTemplate = async () => {
    if (!selectedFileType) {
      showError("Please select a file type first.");
      return;
    }

    setDownloading(true);

    try {
      const response = await downloadAssetTemplate({
        fileType: selectedFileType,
      });

      const url = URL.createObjectURL(response.blob);
      const link = document.createElement("a");
      const defaultFileName = `${selectedFileType.replace(/\s+/g, "-")}-template.xlsx`;

      link.href = url;
      link.download = response.fileName || defaultFileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      showSuccess(
        `${templateFileTypes.find((item) => item.value === selectedFileType)?.label || "Template"} template downloaded successfully.`
      );
    } catch {
      showError("Failed to download template.");
    } finally {
      setDownloading(false);
    }
  };

  const handleProceed = async () => {
    if (!uploadedWorkbook) {
      showError("Select a workbook first.");
      return;
    }

    if (!selectedFileType) {
      showError("Select a file type first.");
      return;
    }

    setProcessing(true);
  setReviewTab(0);
  setReviewPage(1);

    let preProcessBaseline: FolderProvisioningStatus | null = null;
    try {
      preProcessBaseline = await getFolderProvisioningStatus();
    } catch {
      preProcessBaseline = null;
    }

    try {
      const result = await processBulkTemplateUpload(selectedFileType!, uploadedWorkbook);
      setProcessResult(result);
      setValidationResult({
        fileType: result.fileType,
        passedCount: result.rows.filter((row) => row.status === "Passed").length,
        failedCount: result.rows.filter((row) => row.status === "Failed").length,
        rows: result.rows,
      });
      setReviewPage(1);
      showSuccess(
        `Bulk upload completed: ${result.insertedCount} inserted, ${result.failedCount} failed. Folder provisioning started.`
      );

      // Switch to Processing tab and begin polling
      if (result.insertedCount > 0) {
        setReviewTab(2);
        await startPolling(result.insertedCount, preProcessBaseline);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to process workbook.";
      showError(message);
    } finally {
      setProcessing(false);
    }
  };

  const renderRowsContent = (rows: BulkTemplateUploadRowResult[]) => {
    if (rows.length === 0) {
      return (
        <Typography sx={{ color: "#8c8c8c", py: 3 }}>
          No records in this tab.
        </Typography>
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          maxHeight: { xs: "55vh", md: "60vh" },
          overflowY: "auto",
          overflowX: "hidden",
          pr: 0.5,
        }}
      >
        {rows.map((row) => {
          const fieldEntries = row.columns;

          return (
            <Paper
              key={`${row.rowNumber}-${getRowTitle(row)}`}
              variant="outlined"
              sx={{
                p: { xs: 1.5, md: 2 },
                bgcolor: "#171717",
                borderColor: "#2d2d2d",
                color: "#fff",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", sm: "center" },
                  gap: 1,
                  mb: 1.5,
                }}
              >
                <Box>
                  <Typography sx={{ color: "#8c8c8c", fontSize: 12, letterSpacing: 0.6 }}>
                    Row {row.rowNumber}
                  </Typography>
                  <Typography sx={{ color: "#fff", fontSize: 16, fontWeight: 700 }}>
                    {getRowTitle(row)}
                  </Typography>
                </Box>

                <Chip
                  label={row.status}
                  size="small"
                  color={row.status === "Passed" ? "success" : "error"}
                  variant="outlined"
                />
              </Box>

              <Box
                sx={{
                  mb: 1.5,
                  p: 1.25,
                  borderRadius: 1.5,
                  border: "1px solid",
                  borderColor:
                    row.status === "Passed" ? "rgba(76, 175, 80, 0.35)" : "rgba(244, 67, 54, 0.35)",
                  backgroundColor:
                    row.status === "Passed" ? "rgba(76, 175, 80, 0.08)" : "rgba(244, 67, 54, 0.08)",
                }}
              >
                <Typography sx={{ color: "#bdbdbd", fontSize: 12, mb: 0.5 }}>
                  Result
                </Typography>
                <Typography sx={{ color: "#fff", fontSize: 14 }}>
                  {row.message || "-"}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: 1.25,
                }}
              >
                {fieldEntries.map((column) => (
                  <Box
                    key={`${row.rowNumber}-${column.key}`}
                    sx={{
                      minWidth: 0,
                      p: 1.25,
                      borderRadius: 1.25,
                      backgroundColor: "#121212",
                      border: "1px solid #242424",
                    }}
                  >
                    <Typography sx={{ color: "#8c8c8c", fontSize: 12, mb: 0.5 }}>
                      {column.label}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#f1f1f1",
                        fontSize: 13,
                        wordBreak: "break-word",
                      }}
                    >
                      {column.value || "-"}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          );
        })}
      </Box>
    );
  };

  return (
    <Box sx={{ height: "100%", p: { xs: 0, md: 2 } }}>
      <Grid container spacing={2} sx={{ p: { xs: 0, md: 2 } }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Paper
            sx={{
              p: 2.5,
              bgcolor: "#171717",
              color: "#fff",
              border: "1px solid #333",
            }}
          >
            <Typography
              variant="h5"
              sx={{ color: "hsl(19 101% 51% / 1)", mb: 1, fontWeight: 700 }}
            >
              Bulk Template Upload
            </Typography>

            <Box sx={{ mb: 3, pb: 3, borderBottom: "1px solid #2a2a2a" }}>
              <Typography sx={{ color: "#ccc", mb: 1.25, fontWeight: 600 }}>
                Download Template
              </Typography>

              {!selectedFileType && (
                <Alert
                  severity="info"
                  sx={{
                    backgroundColor: "rgba(33, 150, 243, 0.12)",
                    color: "#fff",
                    border: "1px solid rgba(33, 150, 243, 0.35)",
                    marginBottom: 2,
                    "& .MuiAlert-icon": { color: "#2196F3" },
                  }}
                >
                  Please select a file type first to enable download and upload options.
                </Alert>
              )}

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="bulk-template-file-type-label" sx={{ color: "#aaa" }}>
                  File Type
                </InputLabel>
                <Select
                  labelId="bulk-template-file-type-label"
                  value={selectedFileType || ""}
                  label="File Type"
                  onChange={(event) =>
                    setSelectedFileType(event.target.value as AssetTemplateFileType)
                  }
                  sx={{
                    color: "#fff",
                    ".MuiOutlinedInput-notchedOutline": {
                      borderColor: "#555",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#FF6A00",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#FF6A00",
                    },
                    ".MuiSvgIcon-root": {
                      color: "#fff",
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: "#1b1b1b",
                        color: "#fff",
                      },
                    },
                  }}
                >
                  {templateFileTypes.map((fileType) => (
                    <MenuItem key={fileType.value} value={fileType.value}>
                      {fileType.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                fullWidth
                onClick={handleDownloadTemplate}
                disabled={downloading || !selectedFileType}
                startIcon={
                  downloading ? (
                    <CircularProgress size={18} sx={{ color: "#fff" }} />
                  ) : (
                    <DownloadRoundedIcon />
                  )
                }
                sx={{
                  background: "#FF6A00",
                  "&:hover": { background: "#e65c00" },
                  "&:disabled": {
                    background: "rgba(255, 106, 0, 0.45)",
                    color: "#fff",
                  },
                }}
              >
                {downloading ? "Downloading..." : "Download Excel Template"}
              </Button>
            </Box>

            <Box>
              <Typography sx={{ color: "#ccc", mb: 1.25, fontWeight: 600 }}>
                {selectedFileType ? `${getSelectedFileTypeLabel(selectedFileType)} Workbook` : "Workbook"}
              </Typography>
              <Typography sx={{ color: "#8c8c8c", fontSize: 12, mb: 1.5 }}>
                {selectedFileType ? "Upload a workbook in .xlsx format only." : "Select a file type above to enable upload."}
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <input
                  ref={fileInputRef}
                  accept=".xlsx"
                  type="file"
                  style={{ display: "none" }}
                  id="bulk-template-workbook-upload"
                  onChange={handleWorkbookSelected}
                  disabled={!selectedFileType}
                />
                <label htmlFor="bulk-template-workbook-upload">
                  <Button
                    variant="contained"
                    component="span"
                    fullWidth
                    disabled={!selectedFileType}
                    sx={{
                      backgroundColor: selectedFileType ? "hsl(19 101% 51% / 1)" : "rgba(255, 106, 0, 0.45)",
                      color: "#fff",
                      borderColor: "hsl(19 101% 51% / 1)",
                      '&:hover': selectedFileType ? { backgroundColor: "hsl(19 101% 41% / 1)" } : {},
                      cursor: selectedFileType ? "pointer" : "not-allowed",
                    }}
                  >
                    {selectedFileType
                      ? `Upload ${getSelectedFileTypeLabel(selectedFileType)} Workbook`
                      : "Select File Type First"}
                  </Button>
                </label>

                {uploadedWorkbook && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.5,
                      border: "1px solid #333",
                      borderRadius: 1.5,
                      p: 1.25,
                      backgroundColor: "#1f1f1f",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          border: "1px solid #333",
                          borderRadius: 1,
                          width: 44,
                          height: 44,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "#222",
                          flexShrink: 0,
                        }}
                      >
                        <DescriptionIcon sx={{ color: "#bbb" }} />
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            color: "#ddd",
                            fontSize: 13,
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {uploadedWorkbook.name}
                        </Typography>
                        <Typography sx={{ color: "#888", fontSize: 12 }}>
                          Size: {formatFileSize(uploadedWorkbook.size)}
                        </Typography>
                      </Box>
                    </Box>

                    <IconButton
                      size="small"
                      aria-label="Remove uploaded workbook"
                      onClick={handleRemoveWorkbook}
                      sx={{
                        color: "#f44336",
                        border: "1px solid #333",
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}

              </Box>
            </Box>

          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 9 }}>
          <Paper
            sx={{
              p: 2,
              bgcolor: "#111",
              color: "#fff",
              border: "1px solid #333",
            }}
          >
            <Typography variant="h6" sx={{ color: "#fff", mb: 1.5 }}>
              Upload Review
            </Typography>

            {!validationResult && !processResult && !uploadedWorkbook && !folderProcessingActive && (
              <Typography sx={{ color: "#8c8c8c" }}>
                Select a workbook or download a template to continue.
              </Typography>
            )}

            {uploadedWorkbook && !validationResult && (
              <Box sx={{ color: "#d9d9d9" }}>
                <Typography sx={{ mb: 1 }}>
                  Selected workbook: {uploadedWorkbook.name}
                </Typography>
                <Typography sx={{ color: "#8c8c8c", fontSize: 13 }}>
                  File type: {templateFileTypes.find((item) => item.value === selectedFileType)?.label}
                </Typography>
                {validating && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                    <CircularProgress size={16} sx={{ color: "#FF6A00" }} />
                    <Typography sx={{ color: "#FF6A00", fontSize: 13 }}>
                      Validating workbook and preparing review...
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {folderProcessingActive && !validationResult && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Tabs
                  value={0}
                  sx={{
                    mb: 1,
                    '& .MuiTabs-indicator': { backgroundColor: '#FF6A00' },
                  }}
                >
                  <Tab
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={14} sx={{ color: '#FF6A00' }} />
                        Processing
                      </Box>
                    }
                    sx={{ color: '#FF6A00' }}
                  />
                </Tabs>

                <Alert
                  severity="info"
                  sx={{
                    backgroundColor: "rgba(255, 106, 0, 0.10)",
                    color: "#fff",
                    border: "1px solid rgba(255, 106, 0, 0.35)",
                    "& .MuiAlert-icon": { color: "#FF6A00" },
                  }}
                >
                  Folder provisioning is in progress. This status is resumed when you return to this page.
                </Alert>

                {scopedFolderStatus ? (
                  <Box sx={{ px: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography sx={{ color: "#ccc", fontSize: 14, fontWeight: 600 }}>
                        Folder Creation Progress
                      </Typography>
                      <Typography sx={{ color: "#FF6A00", fontSize: 14, fontWeight: 700 }}>
                        {scopedFolderStatus.totalCount - scopedFolderStatus.pendingCount} / {scopedFolderStatus.totalCount}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={scopedFolderStatus.totalCount > 0
                        ? ((scopedFolderStatus.totalCount - scopedFolderStatus.pendingCount) / scopedFolderStatus.totalCount) * 100
                        : 0}
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        backgroundColor: "#2a2a2a",
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 5,
                          backgroundColor: "#FF6A00",
                        },
                      }}
                    />
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
                    <CircularProgress size={18} sx={{ color: "#FF6A00" }} />
                    <Typography sx={{ color: "#ccc", fontSize: 13 }}>
                      Loading folder provisioning status...
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {validationResult && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip label={`Passed ${validationResult.passedCount}`} color="success" variant="outlined" />
                  <Chip label={`Failed ${validationResult.failedCount}`} color="error" variant="outlined" />
                  {processResult && (
                    <Chip
                      label={`Logged ${processResult.processedWorkbookPath}`}
                      variant="outlined"
                      sx={{ color: "#fff", borderColor: "#555" }}
                    />
                  )}
                </Box>

                {(validationResult?.failedCount || 0) > 0 && !processResult && (
                  <Alert
                    severity="warning"
                    sx={{
                      backgroundColor: "rgba(255, 152, 0, 0.12)",
                      color: "#fff",
                      border: "1px solid rgba(255, 152, 0, 0.35)",
                      "& .MuiAlert-icon": { color: "#FF9800" },
                    }}
                  >
                    All records need to be fixed before doing bulk upload. Please review and correct the failed entries.
                  </Alert>
                )}

                {processResult && (
                  <Typography sx={{ color: "#d9d9d9" }}>
                    The processed workbook has been generated and logged to blob storage.
                  </Typography>
                )}

                <Tabs
                  value={reviewTab}
                  onChange={(_, value) => {
                    setReviewTab(value);
                    setReviewPage(1);
                  }}
                  sx={{
                    mb: 1,
                    '& .MuiTabs-indicator': { backgroundColor: '#FF6A00' },
                  }}
                >
                  <Tab label={`Passed (${validationResult.passedCount})`} sx={{ color: '#fff' }} />
                  <Tab label={`Failed (${validationResult.failedCount})`} sx={{ color: '#fff' }} />
                  {folderProcessingActive && (
                    <Tab
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={14} sx={{ color: '#FF6A00' }} />
                          Processing
                        </Box>
                      }
                      sx={{ color: '#FF6A00' }}
                    />
                  )}
                </Tabs>

                {reviewTab === 2 && folderProcessingActive && scopedFolderStatus && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}>
                    <Box sx={{ px: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography sx={{ color: '#ccc', fontSize: 14, fontWeight: 600 }}>
                          Folder Creation Progress
                        </Typography>
                        <Typography sx={{ color: '#FF6A00', fontSize: 14, fontWeight: 700 }}>
                          {scopedFolderStatus.totalCount - scopedFolderStatus.pendingCount} / {scopedFolderStatus.totalCount}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={scopedFolderStatus.totalCount > 0
                          ? ((scopedFolderStatus.totalCount - scopedFolderStatus.pendingCount) / scopedFolderStatus.totalCount) * 100
                          : 0}
                        sx={{
                          height: 10,
                          borderRadius: 5,
                          backgroundColor: '#2a2a2a',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 5,
                            backgroundColor: '#FF6A00',
                          },
                        }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', px: 1 }}>
                      <Paper sx={{ flex: 1, minWidth: 120, p: 2, bgcolor: '#1a1a1a', border: '1px solid #333' }}>
                        <Typography sx={{ color: '#8c8c8c', fontSize: 12, mb: 0.5 }}>Pending</Typography>
                        <Typography sx={{ color: '#FF9800', fontSize: 24, fontWeight: 700 }}>
                          {scopedFolderStatus.pendingCount}
                        </Typography>
                      </Paper>
                      <Paper sx={{ flex: 1, minWidth: 120, p: 2, bgcolor: '#1a1a1a', border: '1px solid #333' }}>
                        <Typography sx={{ color: '#8c8c8c', fontSize: 12, mb: 0.5 }}>Completed</Typography>
                        <Typography sx={{ color: '#4caf50', fontSize: 24, fontWeight: 700 }}>
                          {scopedFolderStatus.completedCount}
                        </Typography>
                      </Paper>
                      <Paper sx={{ flex: 1, minWidth: 120, p: 2, bgcolor: '#1a1a1a', border: '1px solid #333' }}>
                        <Typography sx={{ color: '#8c8c8c', fontSize: 12, mb: 0.5 }}>Failed</Typography>
                        <Typography sx={{ color: '#f44336', fontSize: 24, fontWeight: 700 }}>
                          {scopedFolderStatus.failedCount}
                        </Typography>
                      </Paper>
                    </Box>

                    <Typography sx={{ color: '#555', fontSize: 12, px: 1 }}>
                      Refreshing every 10 seconds...
                    </Typography>
                  </Box>
                )}

                {reviewTab === 2 && folderProcessingActive && !folderStatus && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 3, justifyContent: 'center' }}>
                    <CircularProgress size={20} sx={{ color: '#FF6A00' }} />
                    <Typography sx={{ color: '#ccc' }}>Loading folder provisioning status...</Typography>
                  </Box>
                )}

                <Box sx={{ position: "relative" }}>
                  {reviewTab !== 2 && renderRowsContent(pagedRows)}

                  {reviewTab === 0 && processing && !processResult && (
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 3,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(0, 0, 0, 0.55)",
                        backdropFilter: "blur(1px)",
                        borderRadius: 1,
                        pointerEvents: "auto",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 1.5,
                          px: 3,
                          py: 2.5,
                          borderRadius: 1.5,
                          border: "1px solid rgba(33, 150, 243, 0.35)",
                          backgroundColor: "rgba(12, 20, 26, 0.92)",
                        }}
                      >
                        <CircularProgress size={42} thickness={4.5} sx={{ color: "#2196F3" }} />
                        <Typography sx={{ color: "#fff", fontSize: 14, fontWeight: 600 }}>
                          Bulk Uploading records...
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "stretch", md: "center" },
                    gap: 2,
                    mt: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "stretch", sm: "center" },
                      gap: 1.5,
                    }}
                  >
                    <Typography sx={{ color: "#8c8c8c", fontSize: 13 }}>
                      Showing {activeRows.length === 0 ? 0 : (reviewPage - 1) * reviewPageSize + 1}
                      -{Math.min(reviewPage * reviewPageSize, activeRows.length)} of {activeRows.length}
                    </Typography>

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel id="review-page-size-label" sx={{ color: "#aaa" }}>
                        Rows per page
                      </InputLabel>
                      <Select
                        labelId="review-page-size-label"
                        value={String(reviewPageSize)}
                        label="Rows per page"
                        onChange={(event) => setReviewPageSize(Number(event.target.value))}
                        sx={{
                          color: "#fff",
                          ".MuiOutlinedInput-notchedOutline": { borderColor: "#555" },
                          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#FF6A00" },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#FF6A00" },
                          ".MuiSvgIcon-root": { color: "#fff" },
                        }}
                        MenuProps={{ PaperProps: { sx: { backgroundColor: "#1b1b1b", color: "#fff" } } }}
                      >
                        {reviewPageSizeOptions.map((size) => (
                          <MenuItem key={size} value={String(size)}>{size}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                    <Pagination
                      count={reviewPageCount}
                      page={reviewPage}
                      onChange={(_, value) => setReviewPage(value)}
                      color="primary"
                      shape="rounded"
                      siblingCount={0}
                      boundaryCount={1}
                      sx={{
                        "& .MuiPaginationItem-root": { color: "#fff", borderColor: "#333" },
                        "& .Mui-selected": { backgroundColor: "#FF6A00 !important" },
                      }}
                    />
                    {!processResult && (
                      <Button
                        variant="contained"
                        onClick={handleProceed}
                        disabled={processing || (validationResult?.failedCount || 0) > 0}
                        sx={{
                          backgroundColor: 'hsl(19 101% 51% / 1)',
                          '&:hover': { backgroundColor: 'hsl(19 101% 41% / 1)' },
                          '&:disabled': { backgroundColor: 'rgba(255, 106, 0, 0.35)', color: '#fff' },
                        }}
                      >
                        {processing ? 'Processing...' : 'Proceed'}
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

    </Box>
  );
};

export default BulkTemplateUploadPage;